import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Wine, ArrowRight, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

export default function MfaVerify() {
  const { mfaVerify } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = useRecovery
      ? { recoveryCode: recoveryCode.trim() }
      : { code };
    const r = await mfaVerify(payload);
    setBusy(false);
    if (!r.ok) {
      toast.error(r.error || "Verification failed");
      return;
    }
    toast.success(`Welcome${r.user?.name ? ", " + r.user.name : ""}`);
    navigate("/");
  };

  return (
    <div className="min-h-screen login-canvas grain overflow-hidden">
      <div className="relative z-10 max-w-[480px] mx-auto px-4 py-12 min-h-screen flex flex-col items-center justify-center fade-in">
        <div className="flex items-center gap-3 mb-6">
          <img
            src="/literally-logo.png"
            alt="Literally Vodka"
            className="w-11 h-11 rounded-lg object-cover shadow-soft"
          />
          <div>
            <div className="font-display text-lg text-brand-ink">LiterallyDrink</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-brand-mutedFg">Two-factor verification</div>
          </div>
        </div>

        <div className="card p-8 sm:p-10 w-full" data-testid="mfa-verify-card">
          <div className="label flex items-center gap-1"><ShieldCheck size={12} /> Verify it's you</div>
          <h2 className="font-display text-3xl text-brand-ink mt-1">
            {useRecovery ? "Use a recovery code" : "Enter your 6-digit code"}
          </h2>
          <p className="text-sm text-brand-mutedFg mt-2">
            {useRecovery
              ? "Each recovery code works only once."
              : "Open your authenticator app and enter the current code for LiterallySpirits."}
          </p>

          <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
            {useRecovery ? (
              <label className="block">
                <span className="label">Recovery code</span>
                <input
                  data-testid="mfa-recovery-input"
                  className="input-base mt-1 font-mono tracking-wider"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  placeholder="e.g. a3f9b2c4d1"
                  required
                  autoFocus
                />
              </label>
            ) : (
              <label className="block">
                <span className="label">Authenticator code</span>
                <input
                  data-testid="mfa-verify-code"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  className="input-base mt-1 tracking-[0.5em] font-mono text-center text-2xl"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  autoComplete="one-time-code"
                  required
                  autoFocus
                />
              </label>
            )}

            <button
              type="submit"
              disabled={busy || (!useRecovery && code.length < 6) || (useRecovery && !recoveryCode.trim())}
              className="btn-primary mt-2"
              data-testid="mfa-verify-submit"
            >
              {busy ? (<><Loader2 size={16} className="animate-spin" /> Verifying…</>) : (<>Continue <ArrowRight size={16}/></>)}
            </button>

            <button
              type="button"
              onClick={() => { setUseRecovery((v) => !v); setCode(""); setRecoveryCode(""); }}
              className="btn-ghost text-sm justify-center"
              data-testid="mfa-toggle-recovery"
            >
              <KeyRound size={14} /> {useRecovery ? "Use authenticator code instead" : "Use a recovery code instead"}
            </button>
          </form>

          <div className="text-center text-xs text-brand-mutedFg mt-6">
            <Link to="/login" className="hover:text-brand-ink">← Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
